import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { SupplierAPI, Supplier } from '../../lib/api';
import { OrderItem } from '../../lib/types';
import { Loader2 } from 'lucide-react';

interface AssignSupplierFormProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (supplierId: string, itemIds?: string[], notes?: string) => Promise<void>;
  orderItems: OrderItem[];
}

const AssignSupplierForm: React.FC<AssignSupplierFormProps> = ({
  isOpen,
  onClose,
  onAssign,
  orderItems
}) => {
  const [supplierId, setSupplierId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [itemSelection, setItemSelection] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchResults, setSearchResults] = useState<Supplier[]>([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);

  // Items the admin has ticked for this assignment (only unassigned items are selectable).
  const selectedItemIds = orderItems
    .filter((item) => itemSelection[item._id])
    .map((item) => item._id);
  const selectableCount = orderItems.filter(
    (item) => !(item.supplier || (item.supplierName && item.supplierName !== 'Unknown Supplier'))
  ).length;
  const selectedSupplier = suppliers.find((s) => s._id === supplierId) || null;

  // Initialize item selection
  useEffect(() => {
    const initialSelection = orderItems.reduce((acc, item) => {
      // Default to selected if not already assigned to a supplier
      // An item is assigned if it has a supplier object or a non-"Unknown" supplierName
      const isAssigned = !!(item.supplier ||
        (item.supplierName && item.supplierName !== "Unknown Supplier"));
      acc[item._id] = !isAssigned;
      return acc;
    }, {} as Record<string, boolean>);

    setItemSelection(initialSelection);
  }, [orderItems]);

  // Load suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      setLoading(true);
      try {
        const response = await SupplierAPI.getAllSuppliers({
          isActive: true,
          verificationStatus: 'verified'
        });
        setSuppliers(response.data.data);
        setSearchResults(response.data.data.slice(0, 10)); // Initially show first 10 suppliers
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchSuppliers();
    }
  }, [isOpen]);

  // Filter suppliers based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults(suppliers.slice(0, 10)); // Show first 10 suppliers when no search query
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = suppliers.filter(
        supplier =>
          supplier.businessName.toLowerCase().includes(query) ||
          supplier.ownerName.toLowerCase().includes(query)
      ).slice(0, 10); // Limit to 10 results
      setSearchResults(filtered);
    }
  }, [searchQuery, suppliers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supplierId || isAssigning) return;

    setIsAssigning(true);

    try {
      // If every selectable item is ticked, send undefined (= assign whole order);
      // otherwise send only the explicitly ticked item ids.
      const itemsToAssign =
        selectableCount > 0 && selectedItemIds.length === selectableCount
          ? undefined
          : selectedItemIds;
      await onAssign(supplierId, itemsToAssign, notes);
    } catch (error) {
      console.error("Assignment failed in form:", error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleSupplierSelect = (id: string) => {
    setSupplierId(id);
    const selected = suppliers.find(s => s._id === id);
    setSearchQuery(selected ? selected.businessName : '');
    setShowSupplierDropdown(false); // Hide dropdown after selection
  };

  const handleReset = () => {
    setSupplierId('');
    setNotes('');
    setSearchQuery('');
    setShowSupplierDropdown(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md w-full max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>Assign Order to Supplier</DialogTitle>
          <DialogDescription>
            Select a supplier to assign to this order
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <div className="relative">
                <Input
                  id="supplier"
                  placeholder="Search for supplier..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSupplierDropdown(true);
                  }}
                  onFocus={() => setShowSupplierDropdown(true)}
                  className="h-11" // Larger touch target
                />

                {showSupplierDropdown && searchResults.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto mobile-scrolling">
                    {searchResults.map((supplier) => (
                      <div
                        key={supplier._id}
                        className={`p-2 cursor-pointer hover:bg-gray-100 ${supplierId === supplier._id ? 'bg-blue-50' : ''}`}
                        onClick={() => handleSupplierSelect(supplier._id)}
                      >
                        <div className="font-medium">{supplier.businessName}</div>
                        <div className="text-sm text-gray-500">{supplier.ownerName}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {selectedSupplier && (
              <div className="rounded-md border bg-blue-50/60 p-3 space-y-0.5">
                <div className="text-sm font-semibold text-gray-900">{selectedSupplier.businessName}</div>
                <div className="text-xs text-gray-600">Owner: {selectedSupplier.ownerName}</div>
                {(selectedSupplier as any).mobile && (
                  <div className="text-xs text-gray-600">📞 {(selectedSupplier as any).mobile}</div>
                )}
              </div>
            )}

            {orderItems.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Select Items to Assign</Label>
                  <span className="text-xs text-muted-foreground">
                    {selectedItemIds.length} of {selectableCount} selected
                  </span>
                </div>
                <div className="border rounded-md divide-y max-h-[40vh] overflow-y-auto mobile-scrolling">
                  {orderItems.map((item) => {
                    // Check if this item already has a supplier assigned
                    const isAssigned = !!(item.supplier ||
                      (item.supplierName && item.supplierName !== "Unknown Supplier"));

                    return (
                      <div
                        key={item._id}
                        className={`p-2 ${isAssigned ? 'bg-gray-50' : ''}`}
                      >
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={itemSelection[item._id] || false}
                            onChange={(e) => setItemSelection({
                              ...itemSelection,
                              [item._id]: e.target.checked
                            })}
                            disabled={!!isAssigned}
                            className="rounded h-4 w-4 text-primary focus:ring-primary border-gray-300"
                          />
                          <span>
                            {item.productName || (typeof item.product === 'object' && item.product ? item.product.name : '')}
                            <span className="text-sm text-gray-500 ml-2">
                              ({item.quantity} {item.unit})
                            </span>
                            {isAssigned && (
                              <span className="text-xs font-medium text-blue-600 ml-2">
                                - Already assigned to {
                                  item.assignedSupplier
                                    ? item.assignedSupplier.name
                                    : (item.supplier
                                      ? (item.supplier.name || item.supplier.email)
                                      : item.supplierName)
                                }
                              </span>
                            )}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add notes about this assignment..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <DialogFooter className="sticky bottom-0 bg-background pt-4 pb-0 mt-4 border-t">
              <div className="flex w-full gap-3">
                <Button type="button" variant="outline" onClick={handleReset} className="flex-1">
                  Reset
                </Button>
                <Button
                  type="submit"
                  disabled={!supplierId || isAssigning || (selectableCount > 0 && selectedItemIds.length === 0)}
                  className="flex-1"
                >
                  {isAssigning ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    'Assign Supplier'
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignSupplierForm; 